import type React from 'react';
import { useState } from 'react';
import { Paper, Typography, IconButton, Box, Divider, useTheme } from '@mui/material';
import { LazyQueryExecFunction, OperationVariables } from '@apollo/client';
import { Close, Minimize, Help } from '@mui/icons-material';
import { MessageList } from './messageList';
import { MessageInput } from './messageInput';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

interface ChatWindowProps {
  onClose: () => void;
  loadingResponseModel: boolean;
  sendMessageToModel: LazyQueryExecFunction<any, OperationVariables>;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  onClose,
  sendMessageToModel,
  loadingResponseModel,
}) => {
  const theme = useTheme();

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: '¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte hoy?',
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [fromMessageId, setFromMessageId] = useState<number | null>(null);

  const windowStyles = {
    position: 'absolute' as const,
    bottom: '80px',
    right: '0px',
    width: '380px',
    height: isMinimized ? '60px' : '473px',
    backgroundColor: '#ffffff',
    borderRadius: '12px 12px 12px 12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    transition: 'height 0.3s ease',
  };

  const headerStyles = {
    padding: '16px 20px',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '60px',
    boxSizing: 'border-box' as const,
  };

  const handleSendMessage = async (text: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    const { data } = await sendMessageToModel({
      variables: {
        input: {
          message: text,
          conversationId,
          fromMessageId,
        },
      },
    });

    let aiMessage: Message;
    if (!data?.sendMessageToModel?.iaResponse) {
      aiMessage = {
        id: Date.now().toString(),
        text: 'Lo siento, error al procesar tu solicitud.',
        sender: 'ai',
        timestamp: new Date(),
      };
    } else {
      aiMessage = {
        id: data?.sendMessageToModel?.message_id,
        text: data?.sendMessageToModel?.iaResponse,
        sender: 'ai',
        timestamp: new Date(),
      };
    }

    setConversationId(data?.sendMessageToModel?.conversation_id);
    setFromMessageId(data?.sendMessageToModel?.message_id);
    setMessages((prev) => [...prev, aiMessage]);
  };

  return (
    <Paper sx={windowStyles} elevation={8}>
      <Box sx={headerStyles}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Help sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
            Centro de Ayuda
          </Typography>
        </Box>
        <Box>
          <IconButton
            onClick={() => setIsMinimized(!isMinimized)}
            sx={{ color: 'white', padding: '4px' }}
            size="small">
            <Minimize sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: 'white', padding: '4px' }} size="small">
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {!isMinimized && (
        <>
          <MessageList messages={messages} isLoading={loadingResponseModel} />
          <Divider />
          <MessageInput onSendMessage={handleSendMessage} disabled={loadingResponseModel} />
        </>
      )}
    </Paper>
  );
};
